import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Box, Text } from '@chakra-ui/react'
import { Layout, Container, PageTitle, Content } from 'src/components/Layout'
import List from 'src/components/List/List'
import { handleKeyDown, hasFeatureFlag } from 'src/utils/helpers'
import { postRequest } from 'src/utils/request-client'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon'
import Button from 'src/components/Button/Button'
import DeleteConfirmation from 'src/modals/DeleteConfirmation/index'
import withToast from 'src/components/Toast'
import styles from './UserList.module.less'
import intlMessagesApp from 'src/modules/App/translations'
import intlMessages from '../translations'

class UserList extends React.Component {
  static contextTypes = {
    router: PropTypes.object,
  }

  state = {
    loading: false,
    userToDelete: null,
  }

  componentDidMount() {
    const { history, featureFlags } = this.props

    // TODO: Figure out better way to disable these actions for sub-users
    // Redirect to home page if logged in as sub-user
    // if (user && user.hasOwnProperty('vuaId') && user.vuaId) {
    //   history.push('/')
    // }

    // Redirect to home page if feature flag not enabled or if logged in as sub-user
    if (!hasFeatureFlag(featureFlags, 'users.manage.viewAddRemove') && history) {
      history.push('/')
    }

    // Load users other than currently logged in user
    this.loadData()
  }

  loadData = () => {
    const { getOtherUsers } = this.props
    if (typeof getOtherUsers === 'function') {
      getOtherUsers()
    }
  }

  getUsersName = (forUser, type) => {
    const { intl, user } = this.props

    const isEmail = user && user.email && forUser.email && user.email === forUser.email
    const isSub = user.hasOwnProperty('vuaId') && user.vuaId

    // const isYou = isEmail ? type === 'admin' && !isSub ? true : type === 'user' && isSub ? true : false : false

    let isYou = false
    if (isEmail && ((type === 'admin' && !isSub) || (type === 'user' && isSub))) {
      isYou = true
    }

    const prefix = isYou ? intl.formatMessage(intlMessagesApp.appUsersYou) + ' )' : ''
    const suffix = isYou ? ' ( ' : ''

    return (forUser.name || '-') + suffix + prefix
  }

  getNameInitals = name => {
    return name
      .match(/(\b\S)?/g)
      .join('')
      .match(/(^\S|\S$)?/g)
      .join('')
      .toUpperCase()
  }

  getAdminList = () => {
    const { intl, user } = this.props
    if (!user) {
      return []
    }

    return [
      {
        userId: user.id || null,
        name: this.getUsersName(user, 'admin'),
        image: user.avatar,
        radius: '50%',
        description: intl.formatMessage(intlMessagesApp.appUsersAdmin),
      },
    ]
  }

  getUsersList = () => {
    const { intl, otherUsers } = this.props

    if (!(otherUsers && otherUsers.length > 0)) {
      return []
    }
    return otherUsers.map(user => {
      return {
        userId: user.id,
        email: user.email,
        name: this.getUsersName(user, 'user'),
        nameInitials: user.avatar ? '' : this.getNameInitals(user.name),
        icon: user.avatar,
        radius: '50%',
        description: intl.formatMessage(intlMessagesApp.appUsersUser),
        customRightIcon: () => {
          return <FontAwesomeIconComponent icon={['fal', 'xmark']} size='xs' type='card-font' />
        },
      }
    })
  }

  handleAdminClick = () => {
    const { history } = this.props

    // Go to profile page
    if (history) {
      history.push('/user/profile')
    }
  }

  handleUserClick = user => {
    const { intl, toast } = this.props

    if (!(user && user.hasOwnProperty('userId'))) {
      toast({
        title: intl.formatMessage(intlMessagesApp.appErrorTitle),
        description: intl.formatMessage(intlMessages.messageUserIdNotProvided),
        status: 'error',
      })
    }

    this.setState({
      userToDelete: user,
    })
  }

  handleUserRemoveCanceled = () => {
    this.setState({
      userToDelete: null,
    })
  }

  handleUserRemoveConformed = () => {
    const { intl, toast } = this.props

    const userId = this.state.userToDelete.userId

    this.setState({
      userToDelete: null,
      loading: true,
    })

    postRequest(
      {
        base: '/users/vaultUserAccountRemove',
        mock: false,
      },
      {
        id: userId,
      },
    )
      .then(response => {
        if (response && response.error) {
          this.setState(
            {
              loading: false,
            },
            () => {
              toast({
                title: '',
                description: response.error,
                status: 'success',
              })
            },
          )
        } else {
          this.setState(
            {
              loading: false,
            },
            () => {
              this.loadData()
              toast({
                title: 'User removed successfully',
                status: 'success',
              })
            },
          )
        }
      })
      .catch(e => {
        this.setState(
          {
            loading: false,
          },
          () => {
            toast({
              title: intl.formatMessage(intlMessagesApp.appErrorTitle),
              description: e.error || e.toString(),
              status: 'error',
            })
          },
        )
      })
  }

  renderConfirmationContent = () => {
    const { userToDelete } = this.state

    if (!userToDelete) return

    return (
      <Text>
        <FormattedMessage
          id='app.users.deleteUserConfirmation.part1'
          defaultMessage='You are about to remove {name} from your account.'
          values={{
            name: userToDelete.name ? <b>{userToDelete.name}</b> : 'user',
          }}
        />
        <FormattedMessage
          id='app.users.deleteUserConfirmation.part2'
          defaultMessage='Are you sure?'
        />
      </Text>
    )
  }

  render() {
    const { userToDelete } = this.state
    const { intl, history } = this.props

    return (
      <Layout>
        <Content>
          <PageTitle
            subtitle={<FormattedMessage id='app.profileTab' defaultMessage='Profile' />}
            title={intl.formatMessage(intlMessages.usersHeadline)}
          />
          <Container padder className='global-centered-desktop-content'>
            <Box>
              <List
                attrDataQa='list-users-multiple-accounts list-users-multiple-accounts--admins'
                items={this.getAdminList()}
                onClick={this.handleAdminClick}
                className={styles.list}
                classNameListItem={styles.listItem}
                classNameLeftIconSvg={styles.listLeftIconSvg}
                classNameListItemText={styles.listItemText}
                avatarCircle={true}
                isFullWidth={true}
              />
              <List
                attrDataQa='list-users-multiple-accounts list-users-multiple-accounts--users'
                items={this.getUsersList()}
                onClick={this.handleUserClick}
                className={styles.list}
                classNameListItem={styles.listItem}
                classNameLeftIconSvg={styles.listLeftIconSvg}
                classNameListItemText={styles.listItemText}
                avatarCircle={true}
                isFullWidth={true}
              />
            </Box>
            <Button
              mt={'20px'}
              variant='outline'
              isFullWidth
              onClick={() => history.push('/user/add')}
            >
              <Box mr={'8px'}>
                <FontAwesomeIconComponent
                  icon={['far', 'user-plus']}
                  size='sm'
                  type='text'
                  width={'20'}
                />
              </Box>

              <Text
                fontWeight={500}
                color='text.primary'
                tabIndex='0'
                onKeyDown={handleKeyDown(() => history.push('/user/add'))}
              >
                <FormattedMessage id='app.users.addUser' defaultMessage='Add User' />
              </Text>
            </Button>
          </Container>

          <DeleteConfirmation
            icon={['far', 'user-xmark']}
            heading={'Remove User'}
            confirmText={'Remove'}
            visible={userToDelete}
            onClose={this.handleUserRemoveCanceled}
            renderContent={this.renderConfirmationContent()}
            onConfirm={this.handleUserRemoveConformed}
          />
        </Content>
      </Layout>
    )
  }
}

export default withRouter(withToast(injectIntl(UserList)))
